import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FileDown, Pencil, Plus, Trash2 } from "lucide-react";
import api from "../../../services/apiService";
import DataTable, {
  createActionsColumn,
  DataTableButton,
} from "../../../components/data-table";
import AlertNotification from "../../../components/notification";
import DeleteModal from "../../../components/models/deleteModal.jsx";
import HrPayrollItemModal from "./HrPayrollItemModal";
import { usePageTitle } from "../../../components/layouts/PageTitleContext";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return { Authorization: token ? `Bearer ${token}` : undefined };
};

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const STATUS_BADGE = {
  draft:
    "bg-gray-100 border-gray-200 text-gray-600 dark:bg-slate-500/15 dark:border-slate-500/40 dark:text-slate-300",
  processed:
    "bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-500/15 dark:border-blue-500/40 dark:text-blue-300",
  approved:
    "bg-purple-50 border-purple-200 text-purple-700 dark:bg-purple-500/15 dark:border-purple-500/40 dark:text-purple-300",
  paid: "bg-green-50 border-green-200 text-green-700 dark:bg-green-500/15 dark:border-green-500/40 dark:text-green-300",
  cancelled:
    "bg-red-50 border-red-200 text-red-700 dark:bg-red-500/15 dark:border-red-500/40 dark:text-red-300",
};

const STATUS_FLOW = ["draft", "processed", "approved", "paid"];

function fmt(n) {
  return Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2 });
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-xl border border-app-primary bg-app-card p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-app-muted">
        {label}
      </p>
      <p className="mt-1 text-xl font-bold text-app-primary">{value}</p>
    </div>
  );
}

const downloadPdf = async (url, filename) => {
  const token = localStorage.getItem("token");
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const blob = await res.blob();
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
};

function HrPayrollDetail() {
  const { id } = useParams();
  const [payroll, setPayroll] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isSubmittingItem, setIsSubmittingItem] = useState(false);
  const [itemError, setItemError] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const fetchPayroll = useCallback(async () => {
    try {
      const res = await api.get(`/hr/payrolls/${id}`, {
        headers: authHeaders(),
      });
      setPayroll(res.data);
    } catch {
      setNotification({ text: "Failed to load payroll.", status: false });
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPayroll();
  }, [fetchPayroll]);

  useEffect(() => {
    api
      .get("/hr/employees", { headers: authHeaders() })
      .then((r) => setEmployees(Array.isArray(r.data) ? r.data : []));
  }, []);

  const handleItemSubmit = async (form) => {
    setIsSubmittingItem(true);
    setItemError("");
    try {
      if (editingItem) {
        await api.put(`/hr/payrolls/${id}/items/${editingItem.id}`, form, {
          headers: authHeaders(),
        });
        setNotification({ text: "Item updated.", status: true });
      } else {
        await api.post(`/hr/payrolls/${id}/items`, form, {
          headers: authHeaders(),
        });
        setNotification({ text: "Employee added to payroll.", status: true });
      }
      setShowItemModal(false);
      setEditingItem(null);
      fetchPayroll();
    } catch (err) {
      setItemError(err?.response?.data?.message || "Failed to save.");
    } finally {
      setIsSubmittingItem(false);
    }
  };

  const handleRemoveItem = async () => {
    setIsDeleting(true);
    try {
      await api.delete(`/hr/payrolls/${id}/items/${deleteTarget.id}`, {
        headers: authHeaders(),
      });
      setNotification({ text: "Item removed.", status: true });
      setDeleteTarget(null);
      fetchPayroll();
    } catch {
      setNotification({ text: "Failed to remove item.", status: false });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStatusChange = async (status) => {
    setIsUpdatingStatus(true);
    try {
      await api.put(
        `/hr/payrolls/${id}`,
        { status },
        { headers: authHeaders() },
      );
      fetchPayroll();
    } catch {
      setNotification({ text: "Failed to update status.", status: false });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  usePageTitle(
    payroll ? payroll.title || `${MONTHS[(payroll.month || 1) - 1]} ${payroll.year}` : null,
  );

  if (isLoading)
    return (
      <div className="flex justify-center py-16 text-app-muted">Loading…</div>
    );
  if (!payroll)
    return (
      <div className="flex justify-center py-16 text-app-muted">
        Payroll not found.
      </div>
    );

  const p = payroll;
  const items = p.items || [];
  const monthLabel = `${MONTHS[(p.month || 1) - 1]} ${p.year}`;
  const nextStatus = STATUS_FLOW[STATUS_FLOW.indexOf(p.status) + 1];
  const baseUrl =
    process.env.REACT_APP_API_URL || "https://api.irgoperations.com/api/";

  const money = (key, label, value = (item) => item[key], bold = false) => ({
    key,
    label,
    sortable: true,
    align: "right",
    accessor: value,
    sortValue: (item) => Number(value(item)) || 0,
    render: (v) => (
      <span
        className={`font-mono text-xs${bold ? " font-semibold text-app-primary" : ""}`}
      >
        {fmt(v)}
      </span>
    ),
  });

  const columns = [
    {
      key: "employee.employeeCode",
      label: "Code",
      sortable: true,
      searchable: true,
      render: (v) => (
        <span className="font-mono text-xs text-app-muted">{v || "—"}</span>
      ),
    },
    {
      key: "employee.fullName",
      label: "Employee",
      sortable: true,
      searchable: true,
      render: (v) => (
        <span className="font-medium text-app-primary">{v || "—"}</span>
      ),
    },
    money("basicSalary", "Basic"),
    money("allowances", "Allowances"),
    money("bonus", "Bonus"),
    money(
      "deductions",
      "Deductions",
      (item) => Number(item.deductions) + Number(item.taxDeduction),
    ),
    money("netSalary", "Net", undefined, true),
    {
      key: "paymentStatus",
      label: "Payment",
      sortable: true,
      render: (v) =>  <span
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_BADGE[v] || ""}`}
        >
          {v}
        </span>,
    },
    createActionsColumn(
      (item) => [
        {
          label: "Download payslip",
          icon: FileDown,
          onSelect: () =>
            downloadPdf(
              `${baseUrl}hr/payrolls/${id}/items/${item.id}/pdf`,
              `payslip-${item.employee?.employeeCode || item.id}.pdf`,
            ),
        },
        {
          label: "Edit",
          icon: Pencil,
          onSelect: () => {
            setEditingItem(item);
            setItemError("");
            setShowItemModal(true);
          },
        },
        {
          label: "Remove",
          icon: Trash2,
          tone: "danger",
          onSelect: () => setDeleteTarget(item),
        },
      ],
      { getRowLabel: (item) => item.employee?.fullName || `item ${item.id}` },
    ),
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-app-muted">{monthLabel}</span>
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_BADGE[p.status] || ""}`}
          >
            {p.status}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() =>
              downloadPdf(
                `${baseUrl}hr/payrolls/${id}/pdf`,
                `payroll-${monthLabel}.pdf`,
              )
            }
            className="rounded-lg border border-app-primary bg-app-card px-4 py-2 text-sm font-semibold text-app-secondary transition-colors hover:bg-gray-50 hover:text-app-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 dark:hover:bg-slate-700"
          >
            Download Summary
          </button>
          {nextStatus && !isUpdatingStatus && (
            <button
              onClick={() => handleStatusChange(nextStatus)}
              className="rounded-lg bg-[#2470B7] border border-[#2470B7] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1f5f9b] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 capitalize dark:border-blue-500 dark:bg-blue-600 dark:hover:bg-blue-500"
            >
              Mark as {nextStatus}
            </button>
          )}
          {p.status !== "cancelled" && (
            <button
              onClick={() => handleStatusChange("cancelled")}
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-600 transition-colors hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <SummaryCard label="Employees" value={items.length} />
        <SummaryCard label="Total Gross" value={fmt(p.totalGrossSalary)} />
        <SummaryCard label="Total Deductions" value={fmt(p.totalDeductions)} />
        <SummaryCard label="Total Net" value={fmt(p.totalNetSalary)} />
      </div>

      <DataTable
        ariaLabel="Payroll items"
        columns={columns}
        data={items}
        searchable
        searchPlaceholder="Search code or employee"
        toolbarLeft={
          <h3 className="text-base font-semibold text-app-heading">
            Payroll Items
          </h3>
        }
        toolbarRight={
          <DataTableButton
            variant="primary"
            icon={Plus}
            onClick={() => {
              setEditingItem(null);
              setItemError("");
              setShowItemModal(true);
            }}
          >
            Add Employee
          </DataTableButton>
        }
        pagination
        defaultPageSize={10}
        labels={{ empty: "No employees added yet." }}
      />

      {notification && (
        <AlertNotification
          text={notification.text}
          status={notification.status}
          onClose={() => setNotification(null)}
        />
      )}
      <HrPayrollItemModal
        key={editingItem?.id ?? "new"}
        show={showItemModal}
        onClose={() => {
          if (!isSubmittingItem) {
            setShowItemModal(false);
            setEditingItem(null);
          }
        }}
        onSubmit={handleItemSubmit}
        initial={editingItem}
        employees={
          editingItem
            ? employees
            : employees.filter((e) => !items.some((i) => i.employeeId === e.id))
        }
        isSubmitting={isSubmittingItem}
        error={itemError}
      />
      <DeleteModal
        show={Boolean(deleteTarget)}
        handleClose={() => {
          if (!isDeleting) setDeleteTarget(null);
        }}
        handleConfirm={handleRemoveItem}
        message="Remove this employee from the payroll?"
        loading={isDeleting}
      />
    </div>
  );
}

export default HrPayrollDetail;
